import { timer } from './helpers'

type LoadingScript = HTMLScriptElement & {
    scriptLoadingState?: 'loading' | 'loaded' | 'error'
    scriptLoadingError?: string
}

type LoadScriptErrorStatus = 'timeout' | 'load-error' | 'previously-failed'

export type LoadScriptResult =
    | { status: 'ok'; script: HTMLScriptElement }
    | { status: LoadScriptErrorStatus; message: string }

function buildErrorMessage(scriptName: string, status: LoadScriptErrorStatus, detail: string): string {
    switch (status) {
        case 'timeout':
            return `${scriptName} script load timed out`
        case 'previously-failed':
            return `${scriptName} script was previously failed: ${detail}`
        case 'load-error':
            return `${scriptName} script load failed: ${detail}`
        default:
            return `${scriptName} unknown error: ${detail}`
    }
}

function logScriptError(message: string): void {
    console.error(`[loadScript] ${message}`)
}

function findExistingScript(src: string): LoadingScript | undefined {
    return Array.from(document.scripts).find((scriptElement) => scriptElement.src === src) as LoadingScript | undefined
}

async function waitForExistingScript(existingScript: LoadingScript, scriptName: string): Promise<LoadScriptResult> {
    if (existingScript.scriptLoadingState === 'loaded') {
        return { status: 'ok', script: existingScript }
    }

    if (existingScript.scriptLoadingState === 'error') {
        const message = buildErrorMessage(
            scriptName,
            'previously-failed',
            existingScript.scriptLoadingError || 'unknown error',
        )
        logScriptError(message)
        return { status: 'previously-failed', message }
    }

    const loadResult = await new Promise<{ status: 'ok' } | { status: 'error'; message: string }>((resolve) => {
        existingScript.addEventListener('load', () => {
            existingScript.scriptLoadingState = 'loaded'
            resolve({ status: 'ok' })
        })
        existingScript.addEventListener('error', (errorEvent) => {
            existingScript.scriptLoadingState = 'error'
            resolve({
                status: 'error',
                message: errorEvent.message || (errorEvent.error ? String(errorEvent.error) : 'unknown error'),
            })
        })
    })

    if (loadResult.status === 'ok') {
        return { status: 'ok', script: existingScript }
    }

    existingScript.scriptLoadingError = loadResult.message
    const message = buildErrorMessage(scriptName, 'load-error', loadResult.message)
    logScriptError(message)
    return { status: 'load-error', message }
}

async function createAndLoadScript({
    src,
    scriptName,
    crossorigin,
    timeoutMs,
    attributes,
}: {
    src: string
    scriptName: string
    crossorigin: '' | 'anonymous' | 'use-credentials' | null
    timeoutMs?: number
    attributes: Record<string, string>
}): Promise<LoadScriptResult> {
    const script = document.createElement('script') as LoadingScript
    script.async = true
    script.scriptLoadingState = 'loading'
    script.crossOrigin = crossorigin

    for (const [key, value] of Object.entries(attributes)) {
        script.setAttribute(key, value)
    }

    let loaded = false

    const result = await new Promise<{ status: 'ok' } | { status: 'timeout' } | { status: 'error'; error: unknown }>(
        (resolve) => {
            if (timeoutMs) {
                timer(timeoutMs).then(() => {
                    if (loaded) {
                        return
                    }
                    script.remove()
                    resolve({ status: 'timeout' })
                })
            }

            script.addEventListener('load', () => {
                loaded = true
                resolve({ status: 'ok' })
            })

            script.addEventListener('error', (errorEvent) => {
                if (loaded) {
                    return
                }
                script.remove()
                resolve({
                    status: 'error',
                    error: errorEvent.error || new Error(errorEvent.message || 'Failed to load script'),
                })
            })

            script.src = src
            document.body.appendChild(script)
        },
    )

    if (result.status === 'ok') {
        script.scriptLoadingState = 'loaded'
        return { status: 'ok', script }
    }

    let detail: string
    let status: LoadScriptErrorStatus
    if (result.status === 'timeout') {
        detail = 'Script aborted'
        status = 'timeout'
    } else {
        detail = result.error instanceof Error ? result.error.message : String(result.error)
        status = 'load-error'
    }

    script.scriptLoadingState = 'error'
    script.scriptLoadingError = detail

    const message = buildErrorMessage(scriptName, status, detail)
    logScriptError(message)
    return { status, message }
}

export const loadScript = ({
    src,
    scriptName,
    crossorigin = null,
    timeoutMs,
    useExisting,
    attributes = {},
}: {
    src: string
    scriptName: string
    crossorigin?: '' | 'anonymous' | 'use-credentials' | null
    timeoutMs?: number
    /**
     * It the script with same src already exists, it will be used instead of creating a new one
     */
    useExisting?: boolean
    attributes?: Record<string, string>
}): Promise<LoadScriptResult> => {
    const existingScript = useExisting ? findExistingScript(src) : undefined
    if (existingScript) {
        return waitForExistingScript(existingScript, scriptName)
    }

    return createAndLoadScript({ src, scriptName, crossorigin, timeoutMs, attributes })
}
